/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    Heading,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import { Image } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import {
    IDobbiesTopPicksCarouselConfig,
    IDobbiesTopPicksCarouselProps,
    ITilesData,
    layout
} from './dobbies-top-picks-carousel.props.autogenerated';
export interface IDobbiesTopPicksCarouselViewProps extends IDobbiesTopPicksCarouselProps<{}> {
    isCarousel: boolean;
    GridComponentProps: INodeProps;
    SingleSlideCarouselComponentProps: INodeProps;
    heading?: React.ReactNode;
    TileCollectionContainer: IModuleProps;
    config: IDobbiesTopPicksCarouselConfig;
    tiles: ITilesViewProps[];
}
export interface ITilesViewProps {
    TileContainer: INodeProps;
    Tile: React.ReactNode;
}
/**
 *
 * DobbiesTopPicksCarousel component
 */
class DobbiesTopPicksCarousel extends React.PureComponent<IDobbiesTopPicksCarouselProps<{}>> {
    public render(): JSX.Element | null {
        const { heading, className, tiles } = this.props.config;
        const { resources } = this.props;

        const isCarousel = this.props.config.layout === layout.carousel;
        const headingComponent = heading && (
            <Heading className='dobbies-top-picks-carousel__heading' headingTag={heading.tag} text={heading.text} />
        );
        const tilesCollectionViewProps = {
            ...this.props,
            config: this.props.config,
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'dobbies-top-picks-carousel__items'
            },
            isCarousel: isCarousel,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            TileCollectionContainer: {
                moduleProps: this.props,
                className: classnames('dobbies-top-picks-carousel', className)
            },
            tiles: tiles && tiles.length > 0 && tiles.map((item: ITilesData, index: number) => this._getTiles(item, index))
        };
        return this.props.renderView(tilesCollectionViewProps);
    }
    private _getTiles = (tile: ITilesData, index: number): ITilesViewProps | null => {
        if (!tile) {
            this.props.telemetry.trace('Dobbies Top Picks Carousel: tile is null');
            return null;
        }
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: tile.image && tile.image.imageSettings
        };
        const tileImage = tile.image && <Image {...tile.image} {...imageProps} />;
        // if both null show null
        // if image null dont display anything
        let displaytile;
        if (tile.image) {
            if (tile.actionLink) {
                // if anchor null display only image
                displaytile = (
                    <a
                        key={`link-${index}`}
                        role='button'
                        title={tile.actionLink.linkText}
                        href={tile.actionLink.linkUrl && tile.actionLink.linkUrl.destinationUrl}
                        aria-label={tile.actionLink.ariaLabel}
                        target={tile.actionLink.openInNewTab ? '_blank' : undefined}
                        rel='noreferrer'
                    >
                        {tileImage}
                        <h2 className='dobbies-top-picks-carousel__item__heading'>{tile.headingText}</h2>
                        {/* VSI-Customization -- START 22/10/20
                Added paragraph for image Description (Config: imageDescription)
                */}
                        <p className='dobbies-top-picks-carousel__item__paragraph'>{tile.imageDescription}</p>
                        <span className='dobbies-top-picks-carousel__item__link'>{tile.actionLink.linkText}</span>
                        {/* VSI-Customization -- END  */}
                    </a>
                );
            } else {
                // only image
                displaytile = tileImage;
            }
        } else {
            displaytile = null;
        }
        return {
            TileContainer: {
                tag: 'li' as NodeTag,
                className: 'dobbies-top-picks-carousel__item',
                role: 'listitem',
                key: index
            },
            Tile: displaytile
        };
    };
}

export default DobbiesTopPicksCarousel;
